import React, { useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  HiChevronLeft,
  HiChevronRight,
  HiAcademicCap,
  HiStar,
} from "react-icons/hi";

import { AppWrap, MotionWrap } from "../../wrapper";
import { LoadingSpinner } from "../../components";
import { urlFor } from "../../client";
import { useData } from "../../contexts/DataContext";
import "./Achievements.scss";

const Achievements = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const { achievements, loading, error } = useData();
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  const handleClick = (index) => {
    setCurrentIndex(index);
    setIsAutoPlaying(false);
    // Resume auto-play after user interaction
    setTimeout(() => setIsAutoPlaying(true), 5000);
  };

  const nextSlide = useCallback(() => {
    setCurrentIndex((prev) =>
      prev === achievements.length - 1 ? 0 : prev + 1
    );
  }, [achievements.length]);

  const prevSlide = () => {
    setCurrentIndex((prev) =>
      prev === 0 ? achievements.length - 1 : prev - 1
    );
  };

  // Auto-play functionality
  React.useEffect(() => {
    if (!isAutoPlaying || achievements.length <= 1) return;

    const interval = setInterval(nextSlide, 5000);
    return () => clearInterval(interval);
  }, [isAutoPlaying, achievements.length, currentIndex, nextSlide]);

  if (loading) {
    return <LoadingSpinner message="Loading achievements..." />;
  }

  if (error) {
    return <div className="error-message">Error loading data: {error}</div>;
  }

  const getAchievementIcon = (type) => {
    switch (type?.toLowerCase()) {
      case "award":
      case "trophy":
        return <HiStar className="achievement-icon trophy" />;
      case "education":
      case "certification":
        return <HiAcademicCap className="achievement-icon education" />;
      default:
        return <HiStar className="achievement-icon star" />;
    }
  };

  return (
    <div className="app__achievements-container z-10">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        viewport={{ once: true }}
        className="app__achievements-header"
      >
        <h2 className="head-text">Achievements & Recognition</h2>
        <p className="app__achievements-subtitle">
          Milestones and recognitions that mark my journey
        </p>
      </motion.div>

      {achievements.length > 0 && (
        <div className="app__achievements-showcase">
          {/* Featured Achievement Carousel */}
          <div className="app__achievement-carousel">
            <div className="app__achievement-carousel-container">
              <AnimatePresence mode="wait">
                <motion.div
                  key={`achievement-${currentIndex}`}
                  className="app__achievement-featured"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  transition={{
                    duration: 0.3,
                    ease: "easeInOut",
                  }}
                >
                  <div className="app__achievement-card">
                    <div className="app__achievement-image-container">
                      {getAchievementIcon(achievements[currentIndex]?.type)}
                      <img
                        src={
                          achievements[currentIndex]?.image
                            ? urlFor(achievements[currentIndex].image).url()
                            : require("../../assets/about03.png")
                        }
                        alt={achievements[currentIndex]?.title}
                        className="app__achievement-image"
                      />
                    </div>

                    <div className="app__achievement-content">
                      <div className="app__achievement-badge">
                        {achievements[currentIndex]?.year ||
                          new Date().getFullYear()}
                      </div>

                      <h3 className="app__achievement-title">
                        {achievements[currentIndex]?.title}
                      </h3>

                      <p className="app__achievement-description">
                        {achievements[currentIndex]?.description}
                      </p>

                      {achievements[currentIndex]?.organization && (
                        <div className="app__achievement-org">
                          <span>From:</span>
                          <strong>
                            {achievements[currentIndex].organization}
                          </strong>
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Navigation Controls */}
            {achievements.length > 1 && (
              <div className="app__achievement-controls">
                <button
                  className="app__achievement-nav prev"
                  onClick={prevSlide}
                  aria-label="Previous achievement"
                >
                  <HiChevronLeft />
                </button>

                <div className="app__achievement-indicators">
                  {achievements.map((_, index) => (
                    <button
                      key={index}
                      className={`app__achievement-dot ${
                        index === currentIndex ? "active" : ""
                      }`}
                      onClick={() => handleClick(index)}
                      aria-label={`Go to achievement ${index + 1}`}
                    />
                  ))}
                </div>

                <button
                  className="app__achievement-nav next"
                  onClick={nextSlide}
                  aria-label="Next achievement"
                >
                  <HiChevronRight />
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AppWrap(
  MotionWrap(Achievements, "app__achievements"),
  "achievements",
  "app__whitebg"
);
